import { motion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import { cardHover, withReducedMotion } from '@/lib/animations'

interface FeatureCardProps {
  icon: LucideIcon
  title: string
  description: string
  delay?: number
}

/**
 * Feature card component for landing page
 * Displays an icon, title, and description with hover animation
 */
export function FeatureCard({
  icon: Icon,
  title,
  description,
  delay = 0,
}: FeatureCardProps) {
  return (
    <motion.div
      className="group relative rounded-2xl border border-slate-200 bg-white p-8 transition-shadow hover:shadow-xl dark:border-slate-800 dark:bg-slate-900"
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      viewport={{ once: true, margin: '-100px' }}
      variants={withReducedMotion(cardHover)}
      transition={{ delay }}
    >
      {/* Icon */}
      <div className="mb-5 inline-flex rounded-xl bg-purple-50 p-3 dark:bg-purple-950/30">
        <Icon className="h-8 w-8 text-purple-400" />
      </div>

      {/* Title */}
      <h3 className="mb-3 text-xl font-bold text-slate-900 dark:text-white">
        {title}
      </h3>

      {/* Description */}
      <p className="text-slate-600 dark:text-slate-400">{description}</p>

      {/* Decorative gradient on hover */}
      <div className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
    </motion.div>
  )
}
